import { useSyncExternalStore } from "react";

export type AjaxRequest = {
  id: number;
  method: string;
  url: string;
  status: number | null;
  duration: number | null;
  token: string | null;
  traceId: string | null;
};

const TOKEN_HEADER = "X-Debug-Token";

const TRACE_HEADER = "X-Trace-Id";

// The profiler's own calls are never profiled: keep them off the list.
const OWN_API = /^\/api\/profiler(\/|$)/;

const listeners = new Set<() => void>();

let requests: AjaxRequest[] = [];

let nextId = 1;

let installed = false;

function emit() {
  for (const listener of listeners) {
    listener();
  }
}

function start(method: string, url: string): AjaxRequest | null {
  let path: string;

  try {
    const parsed = new URL(url, window.location.href);

    if (parsed.origin !== window.location.origin) {
      return null;
    }

    path = parsed.pathname;
  } catch {
    return null;
  }

  if (OWN_API.test(path)) {
    return null;
  }

  const request: AjaxRequest = {
    id: nextId++,
    method: method.toUpperCase(),
    url,
    status: null,
    duration: null,
    token: null,
    traceId: null,
  };
  requests = [request, ...requests];
  emit();

  return request;
}

function finish(
  request: AjaxRequest,
  startedAt: number,
  status: number,
  token: string | null,
  traceId: string | null,
) {
  requests = requests.map((entry) =>
    entry.id === request.id
      ? {
          ...entry,
          status: status || null,
          duration: performance.now() - startedAt,
          token: token || null,
          traceId: traceId || null,
        }
      : entry,
  );
  emit();
}

const urls = new WeakMap<XMLHttpRequest, { method: string; url: string }>();

function patchXhr() {
  const Original = window.XMLHttpRequest;

  // A subclass rather than a prototype patch: the legacy loader subclasses
  // XMLHttpRequest itself, and whichever runs first, both keep working.
  window.XMLHttpRequest = class extends Original {
    open(
      method: string,
      url: string | URL,
      async = true,
      username?: string | null,
      password?: string | null,
    ) {
      urls.set(this, { method, url: String(url) });
      super.open(method, url, async, username, password);
    }

    send(body?: Document | XMLHttpRequestBodyInit | null) {
      const info = urls.get(this);
      const request = info ? start(info.method, info.url) : null;

      if (request) {
        const startedAt = performance.now();

        this.addEventListener("loadend", () => {
          finish(
            request,
            startedAt,
            this.status,
            this.getResponseHeader(TOKEN_HEADER),
            this.getResponseHeader(TRACE_HEADER),
          );
        });
      }

      super.send(body);
    }
  };
}

function patchFetch() {
  const original = window.fetch;

  window.fetch = async (input, init) => {
    const url =
      "string" === typeof input
        ? input
        : input instanceof URL
          ? input.href
          : input.url;
    const method =
      init?.method ?? (input instanceof Request ? input.method : "GET");
    const request = start(method, url);
    const startedAt = performance.now();

    try {
      const response = await original(input, init);

      if (request) {
        finish(
          request,
          startedAt,
          response.status,
          response.headers.get(TOKEN_HEADER),
          response.headers.get(TRACE_HEADER),
        );
      }

      return response;
    } catch (error) {
      if (request) {
        finish(request, startedAt, 0, null, null);
      }

      throw error;
    }
  };
}

// Once per page: every later XHR and fetch of the page lands in the list,
// with the profile token its response carries.
export function installAjaxInterception() {
  if (installed) {
    return;
  }

  installed = true;
  patchXhr();
  patchFetch();
}

export function useAjaxRequests(): AjaxRequest[] {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);

      return () => listeners.delete(listener);
    },
    () => requests,
  );
}
